import React, { useEffect, useMemo, useState } from 'react';
import { PlusIcon, SearchIcon, EditIcon, TrashIcon, DownloadIcon, FileSpreadsheetIcon, BriefcaseIcon, LockIcon, TrendingUpIcon, DollarSignIcon, ActivityIcon, UserIcon, BarChart3Icon, XIcon, CalendarIcon, FilterIcon, ListIcon, LayoutGridIcon, ArchiveIcon, ArchiveRestoreIcon, PowerOffIcon, CheckCircleIcon, RotateCcwIcon, XCircleIcon, PieChartIcon, FileTextIcon } from 'lucide-react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell, AreaChart, Area } from 'recharts';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Textarea } from '../ui/Textarea';
import { Modal } from '../ui/Modal';
import { Badge } from '../ui/Badge';
import { Select } from '../ui/Select';
import { Tabs, TabPanel } from '../ui/Tabs';
import { useData } from '../../contexts/DataContext';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import { Service } from '../../types';
import { formatCurrency, getToday, getDoctorPrefix } from '../../utils/helpers';
import { exportServicesToExcel } from '../../utils/excelExport';
import { exportServicesToPdf, exportServiceDetailToPdf, exportDashboardChartDataToPdf } from '../../utils/pdfExport';
import { usePagination } from '../../hooks/usePagination';
import { Pagination } from '../ui/Pagination';
import { hasPermission } from '../../types';
import { ExcelExportModal } from '../ui/ExcelExportModal';
import { PdfExportModal } from '../ui/PdfExportModal';
const COMPARE_COLORS = ['#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4', '#f97316', '#14b8a6', '#6366f1'];
export function Services() {
  const {
    services,
    addService,
    updateService,
    deleteService,
    doctors,
    appointments,
    getDoctor,
    getSpecialty
  } = useData();
  const {
    canCreate,
    canEdit,
    canDelete,
    isReadOnly
  } = usePermissions();
  const readOnly = isReadOnly('services');
  const {
    user
  } = useAuth();
  const isAdmin = user?.role === 'administrador';
  const userRoleNorm = user?.role?.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '') || '';
  const canExportExcel = isAdmin || user?.role === 'terapeuta' || userRoleNorm === 'recepcion' || userRoleNorm === 'recepcionista';
  const canExportPdf = isAdmin || user?.role === 'terapeuta' || userRoleNorm === 'recepcion' || userRoleNorm === 'recepcionista';
  const canViewStats = hasPermission(user?.role || '', 'services_stats', 'read');
  const canDownloadStatsPdf = isAdmin || user?.role === 'supervisor';
  // Tab State
  const [activeTab, setActiveTab] = useState('gestion');
  // Management State
  const [searchTerm, setSearchTerm] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [isActionConfirmOpen, setIsActionConfirmOpen] = useState(false);
  const [actionType, setActionType] = useState<'archive' | 'deactivate' | 'delete' | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isPdfExporting, setIsPdfExporting] = useState(false);
  const [isPdfExportModalOpen, setIsPdfExportModalOpen] = useState(false);
  // Archive/Deactivate Logic
  const STORAGE_KEY = 'savidna-services-status';
  const [itemStatuses, setItemStatuses] = useState<Record<string, 'archived' | 'deactivated'>>({});
  const [viewMode, setViewMode] = useState<'active' | 'archived' | 'deactivated'>('active');
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) setItemStatuses(JSON.parse(saved));
    } catch {}
  }, []);
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(itemStatuses));
  }, [itemStatuses]);
  // Load deactivated/archived statuses for doctors (to filter from assignment)
  const [doctorStatuses, setDoctorStatuses] = useState<Record<string, 'archived' | 'deactivated'>>({});
  useEffect(() => {
    try {
      const saved = localStorage.getItem('savidna-doctors-status');
      if (saved) setDoctorStatuses(JSON.parse(saved));
    } catch {}
    const handleStorage = (e: StorageEvent) => {
      if (e.key === 'savidna-doctors-status' && e.newValue) {
        try {
          setDoctorStatuses(JSON.parse(e.newValue));
        } catch {}
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);
  const isDoctorActive = (id: string) => !doctorStatuses[id];
  const activeDoctors = doctors.filter(d => isDoctorActive(d.id));
  const getItemStatus = (id: string): 'active' | 'archived' | 'deactivated' => itemStatuses[id] || 'active';
  const archiveItem = (id: string) => {
    setItemStatuses(prev => ({
      ...prev,
      [id]: 'archived'
    }));
    updateService(id, {
      status: 'archived'
    } as any).catch(e => console.error('Error archiving service in Supabase:', e));
    setIsActionConfirmOpen(false);
  };
  const deactivateItem = (id: string) => {
    setItemStatuses(prev => ({
      ...prev,
      [id]: 'deactivated'
    }));
    updateService(id, {
      status: 'deactivated'
    } as any).catch(e => console.error('Error deactivating service in Supabase:', e));
    setIsActionConfirmOpen(false);
  };
  const restoreItem = (id: string) => {
    setItemStatuses(prev => {
      const next = {
        ...prev
      };
      delete next[id];
      return next;
    });
    updateService(id, {
      status: 'active'
    } as any).catch(e => console.error('Error restoring service in Supabase:', e));
    setIsActionConfirmOpen(false);
  };
  // Analytics State
  const [compareServiceIds, setCompareServiceIds] = useState<string[]>([]);
  const [compareSearchTerm, setCompareSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState({
    start: '2020-01-01',
    end: new Date().toISOString().split('T')[0]
  });
  const [groupBy, setGroupBy] = useState('month'); // 'day', 'week', 'month'
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    sku: '',
    duration: 30,
    price: '',
    description: '',
    doctorIds: [] as string[]
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  // Filtered services for Management
  const filteredServices = services.filter(s => {
    const matchesSearch = s.name.toLowerCase().includes(searchTerm.toLowerCase());
    const status = getItemStatus(s.id);
    return matchesSearch && status === viewMode;
  });
  const {
    paginatedItems: paginatedServices,
    currentPage: servicesPage,
    totalPages: servicesTotalPages,
    totalItems: servicesTotalItems,
    startIndex: servicesStartIndex,
    endIndex: servicesEndIndex,
    goToPage: servicesGoToPage,
    nextPage: servicesNextPage,
    prevPage: servicesPrevPage
  } = usePagination(filteredServices);
  // Counts for tabs
  const activeCount = services.filter(s => getItemStatus(s.id) === 'active').length;
  const archivedCount = services.filter(s => getItemStatus(s.id) === 'archived').length;
  const deactivatedCount = services.filter(s => getItemStatus(s.id) === 'deactivated').length;
  // Filtered services for Analytics Selector
  const filteredCompareServices = services.filter(s => s.name.toLowerCase().includes(compareSearchTerm.toLowerCase()));
  // --- ANALYTICS CALCULATIONS ---
  // 1. Filter appointments based on date range and status
  const filteredAppointments = useMemo(() => {
    const start = new Date(dateRange.start);
    const end = new Date(dateRange.end);
    end.setHours(23, 59, 59, 999); // End of the day
    return appointments.filter(apt => {
      const aptDate = new Date(apt.date);
      return apt.status === 'completada' && aptDate >= start && aptDate <= end;
    });
  }, [appointments, dateRange]);
  // 2. Aggregate data based on Group By and Selected Services
  const chartData = useMemo(() => {
    const dataMap: Record<string, any> = {};
    // Helper to generate keys based on group by
    const getKey = (date: Date) => {
      if (groupBy === 'day') return date.toISOString().split('T')[0];
      if (groupBy === 'week') {
        const d = new Date(date);
        d.setHours(0, 0, 0, 0);
        d.setDate(d.getDate() + 4 - (d.getDay() || 7));
        const yearStart = new Date(d.getFullYear(), 0, 1);
        const weekNo = Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
        return `${d.getFullYear()}-W${weekNo}`;
      }
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`; // month
    };
    // Initialize map with 0s for selected services if needed, or just build dynamically
    filteredAppointments.forEach(apt => {
      const allServiceIds = apt.serviceIds && apt.serviceIds.length > 0 ? apt.serviceIds : [apt.serviceId].filter(Boolean);
      // If comparing specific services, check if any of the appointment's services match
      if (compareServiceIds.length > 0 && !allServiceIds.some(sId => compareServiceIds.includes(sId))) return;
      const date = new Date(apt.date);
      const key = getKey(date);
      if (!dataMap[key]) {
        dataMap[key] = {
          name: key,
          totalCount: 0,
          totalRevenue: 0
        };
      }
      allServiceIds.forEach(sId => {
        if (compareServiceIds.length > 0 && !compareServiceIds.includes(sId)) return;
        const service = services.find(s => s.id === sId);
        if (service) {
          dataMap[key][service.name] = (dataMap[key][service.name] || 0) + 1;
          dataMap[key][`${service.name}_revenue`] = (dataMap[key][`${service.name}_revenue`] || 0) + service.price;
          dataMap[key].totalCount += 1;
          dataMap[key].totalRevenue += service.price;
        }
      });
    });
    // Sort by date key
    return Object.values(dataMap).sort((a: any, b: any) => a.name.localeCompare(b.name));
  }, [filteredAppointments, groupBy, compareServiceIds, services]);
  // 3. Summary Table Data
  const summaryData = useMemo(() => {
    const summary: Record<string, {
      count: number;
      revenue: number;
      name: string;
    }> = {};
    // Initialize for selected services to ensure they appear even if 0
    if (compareServiceIds.length > 0) {
      compareServiceIds.forEach(id => {
        const s = services.find(ser => ser.id === id);
        if (s) summary[id] = {
          count: 0,
          revenue: 0,
          name: s.name
        };
      });
    } else {
      // If none selected, initialize all
      services.forEach(s => {
        summary[s.id] = {
          count: 0,
          revenue: 0,
          name: s.name
        };
      });
    }
    filteredAppointments.forEach(apt => {
      const allServiceIds = apt.serviceIds && apt.serviceIds.length > 0 ? apt.serviceIds : [apt.serviceId].filter(Boolean);
      if (compareServiceIds.length > 0 && !allServiceIds.some(sId => compareServiceIds.includes(sId))) return;
      allServiceIds.forEach(sId => {
        if (compareServiceIds.length > 0 && !compareServiceIds.includes(sId)) return;
        const service = services.find(s => s.id === sId);
        if (service) {
          if (!summary[service.id]) summary[service.id] = {
            count: 0,
            revenue: 0,
            name: service.name
          };
          summary[service.id].count += 1;
          summary[service.id].revenue += service.price;
        }
      });
    });
    return Object.values(summary).sort((a, b) => b.revenue - a.revenue);
  }, [filteredAppointments, compareServiceIds, services]);
  // 4. KPI Calculations
  const kpiData = useMemo(() => {
    const totalRevenue = summaryData.reduce((sum, item) => sum + item.revenue, 0);
    const totalCount = summaryData.reduce((sum, item) => sum + item.count, 0);
    const topService = summaryData.length > 0 ? summaryData[0] : null;
    const avgTicket = totalCount > 0 ? totalRevenue / totalCount : 0;
    return {
      totalRevenue,
      totalCount,
      topService,
      avgTicket
    };
  }, [summaryData]);
  // Pagination for summary table in statistics tab
  const {
    paginatedItems: paginatedSummary,
    currentPage: summaryPage,
    totalPages: summaryTotalPages,
    totalItems: summaryTotalItems,
    startIndex: summaryStartIndex,
    endIndex: summaryEndIndex,
    goToPage: summaryGoToPage,
    nextPage: summaryNextPage,
    prevPage: summaryPrevPage
  } = usePagination(summaryData, {
    itemsPerPage: 10
  });
  // --- HANDLERS ---
  const openNewForm = () => {
    if (!canCreate('services')) return;
    setSelectedService(null);
    setFormData({
      name: '',
      sku: '',
      duration: 30,
      price: '',
      description: '',
      doctorIds: []
    });
    setFormErrors({});
    setIsFormOpen(true);
  };
  const openEditForm = (service: Service) => {
    if (!canEdit('services')) return;
    setSelectedService(service);
    setFormData({
      name: service.name,
      sku: service.sku || '',
      duration: service.duration,
      price: String(service.price),
      description: service.description,
      doctorIds: service.doctorIds || []
    });
    setFormErrors({});
    setIsFormOpen(true);
  };
  const openActionConfirm = (service: Service, type: 'archive' | 'deactivate' | 'delete') => {
    setSelectedService(service);
    setActionType(type);
    setIsActionConfirmOpen(true);
  };
  const handleAction = () => {
    if (!selectedService || !actionType) return;
    if (actionType === 'archive') {
      archiveItem(selectedService.id);
    } else if (actionType === 'deactivate') {
      deactivateItem(selectedService.id);
    } else if (actionType === 'delete') {
      deleteService(selectedService.id);
      setIsActionConfirmOpen(false);
    }
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errors: Record<string, string> = {};
    if (!formData.name) errors.name = 'El nombre es requerido';
    if (formData.duration <= 0) errors.duration = 'La duración debe ser mayor a 0';
    const priceNum = parseInt(formData.price, 10);
    if (isNaN(priceNum) || priceNum < 0) errors.price = 'El precio no puede ser negativo';
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    const dataToSave = {
      name: formData.name,
      sku: formData.sku || undefined,
      duration: formData.duration,
      price: priceNum,
      description: formData.description,
      doctorIds: formData.doctorIds
    };
    try {
      // ESTO ES LO QUE HACE QUE SE GUARDE:
      if (selectedService) {
        await updateService(selectedService.id, dataToSave);
      } else {
        await addService(dataToSave);
      }
      // Solo se cierra el cuadro si la base de datos respondió "OK"
      setIsFormOpen(false);
    } catch (error) {
      console.error('Error al guardar:', error);
      alert('No se pudo guardar. Revisa tu internet o si el SKU ya existe.');
    }
  };
  const handleExportToExcel = async (dateFrom: string | null, dateTo: string | null) => {
    setIsExporting(true);
    await new Promise(resolve => setTimeout(resolve, 500));
    exportServicesToExcel(services);
    setIsExporting(false);
    setIsExportModalOpen(false);
  };
  const handleExportToPdf = async (dateFrom: string | null, dateTo: string | null) => {
    setIsPdfExporting(true);
    await new Promise(resolve => setTimeout(resolve, 500));
    exportServicesToPdf(services);
    setIsPdfExporting(false);
    setIsPdfExportModalOpen(false);
  };
  const toggleDoctorSelection = (doctorId: string) => {
    setFormData(prev => {
      const currentIds = prev.doctorIds || [];
      return currentIds.includes(doctorId) ? {
        ...prev,
        doctorIds: currentIds.filter(id => id !== doctorId)
      } : {
        ...prev,
        doctorIds: [...currentIds, doctorId]
      };
    });
  };
  const toggleCompareService = (serviceId: string) => {
    setCompareServiceIds(prev => prev.includes(serviceId) ? prev.filter(id => id !== serviceId) : [...prev, serviceId]);
  };
  const tabs = [{
    id: 'gestion',
    label: 'Gestión de Servicios',
    icon: <LayoutGridIcon className="w-4 h-4" data-id="element-3456" />
  }, ...(canViewStats ? [{
    id: 'estadisticas',
    label: 'Estadísticas y Reportes',
    icon: <BarChart3Icon className="w-4 h-4" data-id="element-3457" />
  }] : [])];
  return <div className="space-y-6" data-id="element-3458">
      {/* Header Banner */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-purple-600 via-purple-500 to-indigo-500 p-6 text-white shadow-xl" data-id="element-3459">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" data-id="element-3460" />
        <div className="relative z-10 flex flex-col md:flex-row md:items-center md:justify-between gap-4" data-id="element-3461">
          <div data-id="element-3462">
            <div className="flex items-center gap-3 mb-2" data-id="element-3463">
              <div className="p-2.5 bg-white/15 backdrop-blur-sm rounded-xl" data-id="element-3464">
                <BriefcaseIcon className="w-6 h-6" data-id="element-3465" />
              </div>
              <h1 className="text-2xl font-bold" data-id="element-3466">Servicios</h1>
            </div>
            <p className="text-purple-100 max-w-xl" data-id="element-3467">
              Administración y análisis de servicios médicos
            </p>
          </div>
          <div className="flex gap-3" data-id="element-3468">
            {canExportPdf && <Button variant="outline" onClick={() => setIsPdfExportModalOpen(true)} isLoading={isPdfExporting} className="bg-white/10 border-white/30 text-white hover:bg-white/20 backdrop-blur-sm" leftIcon={<BriefcaseIcon className="w-4 h-4" data-id="element-3470" />} data-id="element-3469">
                Exportar PDF
              </Button>}
            {canExportExcel && <Button variant="outline" onClick={() => setIsExportModalOpen(true)} isLoading={isExporting} className="bg-white/10 border-white/30 text-white hover:bg-white/20 backdrop-blur-sm" leftIcon={<DownloadIcon className="w-4 h-4" data-id="element-3472" />} data-id="element-3471">
                Exportar Excel
              </Button>}
          </div>
        </div>
      </div>

      <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} data-id="element-3473">
        {/* ==================== TAB 1: GESTIÓN ==================== */}
        <TabPanel id="gestion" activeTab={activeTab} data-id="element-3474">
          <div className="space-y-6" data-id="element-3475">
            {/* Actions Bar */}
            <div className="flex flex-col md:flex-row gap-4 justify-between items-end" data-id="element-3476">
              <div className="flex flex-col sm:flex-row gap-4 w-full md:w-auto" data-id="element-3477">
                <div className="w-full sm:w-64 relative" data-id="element-3478">
                  <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-3479" />
                  <Input placeholder="Buscar servicios..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-9" data-id="element-3480" />
                </div>

                {canCreate('services') && <Button leftIcon={<PlusIcon className="w-4 h-4" data-id="element-3482" />} onClick={openNewForm} data-id="element-3481">
                    Nuevo Servicio
                  </Button>}
              </div>

              {/* View Mode Tabs */}
              <div className="flex items-center gap-1 p-1 bg-gray-100 dark:bg-gray-800 rounded-xl w-full md:w-auto overflow-x-auto" data-id="element-3483">
                {[{
                key: 'active',
                label: 'Activos',
                count: activeCount,
                icon: CheckCircleIcon
              }, {
                key: 'archived',
                label: 'Archivados',
                count: archivedCount,
                icon: ArchiveIcon
              }, {
                key: 'deactivated',
                label: 'Desactivados',
                count: deactivatedCount,
                icon: XCircleIcon
              }].map(tab => <button key={tab.key} onClick={() => setViewMode(tab.key as any)} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${viewMode === tab.key ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`} data-id="element-3484">
                    <tab.icon className="w-4 h-4" data-id="element-3485" />
                    {tab.label}
                    <span className="text-xs bg-gray-200 dark:bg-gray-600 px-1.5 py-0.5 rounded-full" data-id="element-3486">
                      {tab.count}
                    </span>
                  </button>)}
              </div>
            </div>

            {readOnly && <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-amber-700 dark:text-amber-300 text-sm flex items-center gap-2" data-id="element-3487">
                <LockIcon className="w-4 h-4" data-id="element-3488" />
                <span data-id="element-3489">
                  Solo tienes permisos de lectura. No puedes crear, editar o
                  eliminar servicios.
                </span>
              </div>}

            {/* Services Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-3490">
              {paginatedServices.length === 0 ? <div className="col-span-full text-center py-16 text-gray-500 dark:text-gray-400" data-id="element-3491">
                  <div className="flex flex-col items-center justify-center" data-id="element-3492">
                    <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4" data-id="element-3493">
                      <BriefcaseIcon className="w-8 h-8 text-gray-400" data-id="element-3494" />
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1" data-id="element-3495">
                      No se encontraron servicios
                    </h3>
                    <p className="text-sm" data-id="element-3496">
                      {searchTerm ? 'Intenta con otros términos de búsqueda' : viewMode === 'active' ? 'Comienza creando un nuevo servicio' : 'No hay servicios en esta vista'}
                    </p>
                  </div>
                </div> : paginatedServices.map(service => <Card key={service.id} className="flex flex-col h-full hover:shadow-md transition-shadow relative overflow-hidden group" data-id="element-3497">
                    <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-purple-500 to-indigo-500" data-id="element-3498" />
                    <div className="flex items-start justify-between mb-4 mt-2" data-id="element-3499">
                      <div data-id="element-3500">
                        <h3 className="font-semibold text-gray-900 dark:text-white text-lg" data-id="element-3501">
                          {service.name}
                        </h3>
                        <div className="flex items-center gap-2 mt-1" data-id="element-3502">
                          <Badge variant="info" className="text-xs" data-id="element-3503">
                            {service.duration} min
                          </Badge>
                          <span className="text-xl font-bold text-purple-600 dark:text-purple-400" data-id="element-3504">
                            {formatCurrency(service.price)}
                          </span>
                        </div>
                      </div>
                      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-3505">
                        {!readOnly && viewMode === 'active' ? <>
                            {canEdit('services') && <button onClick={() => openEditForm(service)} className="p-2 text-gray-500 hover:text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg transition-colors" title="Editar servicio" data-id="element-3506">
                                <EditIcon className="w-4 h-4" data-id="element-3507" />
                              </button>}
                            {canDelete('services') && <>
                                <button onClick={() => openActionConfirm(service, 'archive')} className="p-2 text-gray-500 hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded-lg transition-colors" title="Archivar" data-id="element-3508">
                                  <ArchiveIcon className="w-4 h-4" data-id="element-3509" />
                                </button>
                                <button onClick={() => openActionConfirm(service, 'deactivate')} className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors" title="Desactivar" data-id="element-3510">
                                  <PowerOffIcon className="w-4 h-4" data-id="element-3511" />
                                </button>
                              </>}
                          </> : !readOnly && viewMode !== 'active' ? <button onClick={() => restoreItem(service.id)} className="p-2 text-gray-500 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors" title="Restaurar" data-id="element-3512">
                            <RotateCcwIcon className="w-4 h-4" data-id="element-3513" />
                          </button> : null}
                      </div>
                    </div>
                    {service.sku && <p className="text-xs text-gray-400 dark:text-gray-500 font-mono mb-2" data-id="element-3514">
                        SKU: {service.sku}
                      </p>}
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 flex-grow line-clamp-3" data-id="element-3515">
                      {service.description || 'Sin descripción'}
                    </p>
                    <div className="pt-4 border-t border-gray-100 dark:border-gray-700 mt-auto" data-id="element-3516">
                      <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 flex items-center gap-1" data-id="element-3517">
                        <UserIcon className="w-3 h-3" data-id="element-3518" /> Doctores Asignados:
                      </p>
                      <div className="flex flex-wrap gap-2" data-id="element-3519">
                        {service.doctorIds && service.doctorIds.length > 0 ? service.doctorIds.map(docId => {
                    const doctor = getDoctor(docId);
                    return doctor ? <span key={docId} className="inline-flex items-center px-2 py-1 rounded-md bg-gray-100 dark:bg-gray-800 text-xs text-gray-700 dark:text-gray-300" data-id="element-3520">
                                {getDoctorPrefix(doctor.gender, getSpecialty(doctor.specialtyId)?.name)}{' '}
                                {doctor.name}
                              </span> : null;
                  }) : <span className="text-xs text-gray-400 italic" data-id="element-3521">
                            Ningún doctor asignado
                          </span>}
                      </div>
                    </div>
                  </Card>)}
            </div>

            {/* Pagination */}
            <div className="pt-4 border-t border-gray-100 dark:border-gray-700" data-id="element-3522">
              <Pagination currentPage={servicesPage} totalPages={servicesTotalPages} totalItems={servicesTotalItems} startIndex={servicesStartIndex} endIndex={servicesEndIndex} onPageChange={servicesGoToPage} onNext={servicesNextPage} onPrev={servicesPrevPage} data-id="element-3523" />
            </div>
          </div>
        </TabPanel>

        {/* ==================== TAB 2: ESTADÍSTICAS ==================== */}
        <TabPanel id="estadisticas" activeTab={activeTab} data-id="element-3524">
          <div className="space-y-6" data-id="element-3525">
            {/* KPI Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-3526">
              <Card className="bg-gradient-to-br from-purple-500 to-indigo-600 text-white border-none" data-id="element-3527">
                <div className="flex items-center justify-between mb-2" data-id="element-3528">
                  <p className="text-purple-100 text-sm font-medium" data-id="element-3529">
                    Ingresos Totales
                  </p>
                  <DollarSignIcon className="w-5 h-5 text-purple-200" data-id="element-3530" />
                </div>
                <p className="text-2xl font-bold" data-id="element-3531">
                  {formatCurrency(kpiData.totalRevenue)}
                </p>
                <p className="text-xs text-purple-200 mt-1" data-id="element-3532">
                  En el periodo seleccionado
                </p>
              </Card>

              <Card className="bg-white dark:bg-gray-800 border-l-4 border-l-emerald-500" data-id="element-3533">
                <div className="flex items-center justify-between mb-2" data-id="element-3534">
                  <p className="text-gray-500 dark:text-gray-400 text-sm font-medium" data-id="element-3535">
                    Citas Completadas
                  </p>
                  <CheckCircleIcon className="w-5 h-5 text-emerald-500" data-id="element-3536" />
                </div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-3537">
                  {kpiData.totalCount}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1" data-id="element-3538">
                  Servicios realizados
                </p>
              </Card>

              <Card className="bg-white dark:bg-gray-800 border-l-4 border-l-blue-500" data-id="element-3539">
                <div className="flex items-center justify-between mb-2" data-id="element-3540">
                  <p className="text-gray-500 dark:text-gray-400 text-sm font-medium" data-id="element-3541">
                    Ticket Promedio
                  </p>
                  <ActivityIcon className="w-5 h-5 text-blue-500" data-id="element-3542" />
                </div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-3543">
                  {formatCurrency(kpiData.avgTicket)}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1" data-id="element-3544">
                  Por servicio realizado
                </p>
              </Card>

              <Card className="bg-white dark:bg-gray-800 border-l-4 border-l-amber-500" data-id="element-3545">
                <div className="flex items-center justify-between mb-2" data-id="element-3546">
                  <p className="text-gray-500 dark:text-gray-400 text-sm font-medium" data-id="element-3547">
                    Top Servicio
                  </p>
                  <TrendingUpIcon className="w-5 h-5 text-amber-500" data-id="element-3548" />
                </div>
                <p className="text-lg font-bold text-gray-900 dark:text-white truncate" title={kpiData.topService?.name || '-'} data-id="element-3549">
                  {kpiData.topService?.name || '-'}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1" data-id="element-3550">
                  {kpiData.topService ? formatCurrency(kpiData.topService.revenue) : '$0'}{' '}
                  generados
                </p>
              </Card>
            </div>

            {/* 1. FILTERS SECTION */}
            <Card data-id="element-3551">
              <CardHeader className="border-b border-gray-100 dark:border-gray-700 pb-4" data-id="element-3552">
                <CardTitle className="flex items-center gap-2 text-base" data-id="element-3553">
                  <FilterIcon className="w-4 h-4" data-id="element-3554" /> Configuración del Análisis
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6" data-id="element-3555">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-3556">
                  {/* Date Range */}
                  <div className="space-y-2" data-id="element-3557">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-3558">
                      Rango de Fecha
                    </label>
                    <div className="flex items-center gap-2" data-id="element-3559">
                      <Input type="date" value={dateRange.start} onChange={e => setDateRange(prev => ({
                      ...prev,
                      start: e.target.value
                    }))} max={dateRange.end || getToday()} className="bg-gray-50 dark:bg-gray-900" data-id="element-3560" />
                      <span className="text-gray-400" data-id="element-3561">-</span>
                      <Input type="date" value={dateRange.end} onChange={e => setDateRange(prev => ({
                      ...prev,
                      end: e.target.value
                    }))} max={getToday()} className="bg-gray-50 dark:bg-gray-900" data-id="element-3562" />
                    </div>
                  </div>

                  {/* Group By */}
                  <div className="space-y-2" data-id="element-3563">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-3564">
                      Agrupar Por
                    </label>
                    <Select value={groupBy} onChange={setGroupBy} options={[{
                    value: 'day',
                    label: 'Día'
                  }, {
                    value: 'week',
                    label: 'Semana'
                  }, {
                    value: 'month',
                    label: 'Mes'
                  }]} data-id="element-3565" />
                  </div>

                  {/* Service Selector */}
                  <div className="space-y-2" data-id="element-3566">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-3567">
                      Comparar Servicios
                    </label>
                    <div className="relative" data-id="element-3568">
                      <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-3569" />
                      <input type="text" placeholder="Buscar servicios..." value={compareSearchTerm} onChange={e => setCompareSearchTerm(e.target.value)} className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all" data-id="element-3570" />
                    </div>

                    {/* Selected Tags */}
                    {compareServiceIds.length > 0 && <div className="flex flex-wrap gap-2 mt-2" data-id="element-3571">
                        {compareServiceIds.map((id, index) => {
                      const svc = services.find(s => s.id === id);
                      if (!svc) return null;
                      return <span key={id} className="inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium text-white shadow-sm" style={{
                        backgroundColor: COMPARE_COLORS[index % COMPARE_COLORS.length]
                      }} data-id="element-3572">
                              {svc.name}
                              <button onClick={() => toggleCompareService(id)} className="hover:opacity-75" data-id="element-3573">
                                <XIcon className="w-3 h-3" data-id="element-3574" />
                              </button>
                            </span>;
                    })}
                        <button onClick={() => setCompareServiceIds([])} className="text-xs text-gray-500 hover:text-red-500 px-1 font-medium" data-id="element-3575">
                          Limpiar
                        </button>
                      </div>}

                    {/* Dropdown list */}
                    <div className="max-h-32 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg mt-2 p-1 bg-white dark:bg-gray-800 shadow-sm" data-id="element-3576">
                      {filteredCompareServices.map(service => <label key={service.id} className="flex items-center gap-2 p-2 hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded cursor-pointer transition-colors" data-id="element-3577">
                          <input type="checkbox" checked={compareServiceIds.includes(service.id)} onChange={() => toggleCompareService(service.id)} className="w-4 h-4 text-purple-600 rounded border-gray-300 focus:ring-purple-500" data-id="element-3578" />
                          <span className="text-sm text-gray-700 dark:text-gray-300 truncate" data-id="element-3579">
                            {service.name}
                          </span>
                        </label>)}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* 2. CHARTS SECTION */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-3580">
              {/* Quantity Chart */}
              <Card data-id="element-3581">
                <CardHeader data-id="element-3582">
                  <div className="flex items-center justify-between" data-id="element-3583">
                    <CardTitle className="flex items-center gap-2" data-id="element-3584">
                      <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg" data-id="element-3585">
                        <BarChart3Icon className="w-5 h-5 text-blue-600 dark:text-blue-400" data-id="element-3586" />
                      </div>
                      Tendencia de Servicios
                    </CardTitle>
                    {canDownloadStatsPdf && chartData.length > 0 && <button onClick={() => {
                    const cols = [{
                      header: 'Periodo',
                      key: 'periodo'
                    }, {
                      header: 'Total Servicios',
                      key: 'total'
                    }];
                    const rows = chartData.map((d: any) => ({
                      label: d.name,
                      values: {
                        periodo: d.name,
                        total: d.totalCount
                      }
                    }));
                    exportDashboardChartDataToPdf(rows, cols, 'Tendencia de Servicios', {
                      from: dateRange.start,
                      to: dateRange.end
                    });
                  }} className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors" title="Descargar PDF" data-id="element-3587">
                        <DownloadIcon className="w-4 h-4" data-id="element-3588" />
                      </button>}
                  </div>
                </CardHeader>
                <CardContent data-id="element-3589">
                  <div className="h-[300px] w-full" data-id="element-3590">
                    <ResponsiveContainer width="100%" height="100%" data-id="element-3591">
                      <AreaChart data={chartData} margin={{
                      top: 10,
                      right: 10,
                      left: 0,
                      bottom: 0
                    }} data-id="element-3592">
                        <defs data-id="element-3593">
                          <linearGradient id="colorTotal" x1="0" y1="0" x2="0" y2="1" data-id="element-3594">
                            <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} data-id="element-3595" />
                            <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} data-id="element-3596" />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} data-id="element-3597" />
                        <XAxis dataKey="name" stroke="#6b7280" fontSize={12} tickLine={false} axisLine={false} data-id="element-3598" />
                        <YAxis stroke="#6b7280" fontSize={12} allowDecimals={false} tickLine={false} axisLine={false} data-id="element-3599" />
                        <Tooltip contentStyle={{
                        backgroundColor: 'white',
                        borderRadius: '12px',
                        border: 'none',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                      }} data-id="element-3600" />
                        <Legend wrapperStyle={{
                        paddingTop: '20px'
                      }} data-id="element-3601" />
                        {compareServiceIds.length > 0 ? compareServiceIds.map((id, index) => {
                        const svc = services.find(s => s.id === id);
                        if (!svc) return null;
                        return <Area key={id} type="monotone" dataKey={svc.name} stroke={COMPARE_COLORS[index % COMPARE_COLORS.length]} fill={`url(#color${index})`} // Simplified for demo, ideally unique IDs
                        strokeWidth={2} fillOpacity={0.1} data-id="element-3602" />;
                      }) : <Area type="monotone" dataKey="totalCount" name="Total Servicios" stroke="#3b82f6" fill="url(#colorTotal)" strokeWidth={3} data-id="element-3603" />}
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Revenue Chart */}
              <Card data-id="element-3604">
                <CardHeader data-id="element-3605">
                  <div className="flex items-center justify-between" data-id="element-3606">
                    <CardTitle className="flex items-center gap-2" data-id="element-3607">
                      <div className="p-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg" data-id="element-3608">
                        <DollarSignIcon className="w-5 h-5 text-emerald-600 dark:text-emerald-400" data-id="element-3609" />
                      </div>
                      Ingresos Generados
                    </CardTitle>
                    {canDownloadStatsPdf && chartData.length > 0 && <button onClick={() => {
                    const cols = [{
                      header: 'Periodo',
                      key: 'periodo'
                    }, {
                      header: 'Ingresos Totales',
                      key: 'ingresos'
                    }];
                    const fmtCurrency = (n: number) => new Intl.NumberFormat('es-DO', {
                      style: 'currency',
                      currency: 'DOP',
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0
                    }).format(n);
                    const rows = chartData.map((d: any) => ({
                      label: d.name,
                      values: {
                        periodo: d.name,
                        ingresos: fmtCurrency(d.totalRevenue)
                      }
                    }));
                    exportDashboardChartDataToPdf(rows, cols, 'Ingresos Generados por Servicios', {
                      from: dateRange.start,
                      to: dateRange.end
                    });
                  }} className="p-2 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 rounded-lg transition-colors" title="Descargar PDF" data-id="element-3610">
                        <DownloadIcon className="w-4 h-4" data-id="element-3611" />
                      </button>}
                  </div>
                </CardHeader>
                <CardContent data-id="element-3612">
                  <div className="h-[300px] w-full" data-id="element-3613">
                    <ResponsiveContainer width="100%" height="100%" data-id="element-3614">
                      <BarChart data={chartData} margin={{
                      top: 10,
                      right: 10,
                      left: 0,
                      bottom: 0
                    }} data-id="element-3615">
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} data-id="element-3616" />
                        <XAxis dataKey="name" stroke="#6b7280" fontSize={12} tickLine={false} axisLine={false} data-id="element-3617" />
                        <YAxis stroke="#6b7280" fontSize={12} tickFormatter={v => `$${v}`} tickLine={false} axisLine={false} data-id="element-3618" />
                        <Tooltip cursor={{
                        fill: 'rgba(0,0,0,0.05)'
                      }} contentStyle={{
                        backgroundColor: 'white',
                        borderRadius: '12px',
                        border: 'none',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                      }} formatter={(value: number) => [formatCurrency(value), '']} data-id="element-3619" />
                        <Legend wrapperStyle={{
                        paddingTop: '20px'
                      }} data-id="element-3620" />
                        {compareServiceIds.length > 0 ? compareServiceIds.map((id, index) => {
                        const svc = services.find(s => s.id === id);
                        if (!svc) return null;
                        return <Bar key={id} dataKey={`${svc.name}_revenue`} name={svc.name} fill={COMPARE_COLORS[index % COMPARE_COLORS.length]} radius={[4, 4, 0, 0]} stackId="a" data-id="element-3621" />;
                      }) : <Bar dataKey="totalRevenue" name="Total Ingresos" fill="#10b981" radius={[6, 6, 0, 0]} data-id="element-3622" />}
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* 3. SUMMARY TABLE */}
            <Card className="overflow-hidden border-none shadow-lg" data-id="element-3623">
              <div className="h-1 bg-gradient-to-r from-purple-500 to-indigo-500" data-id="element-3624" />
              <CardHeader data-id="element-3625">
                <div className="flex items-center justify-between" data-id="element-3626">
                  <CardTitle className="flex items-center gap-2" data-id="element-3627">
                    <ListIcon className="w-5 h-5 text-gray-600" data-id="element-3628" />
                    Detalle por Servicio
                  </CardTitle>
                  {canDownloadStatsPdf && summaryData.length > 0 && <button onClick={() => {
                  const detailData = summaryData.map(item => ({
                    name: item.name,
                    count: item.count,
                    totalRevenue: item.revenue,
                    avgPerAppointment: item.count > 0 ? item.revenue / item.count : 0,
                    percentOfTotal: kpiData.totalRevenue > 0 ? item.revenue / kpiData.totalRevenue * 100 : 0
                  }));
                  exportServiceDetailToPdf(detailData, kpiData.totalRevenue, kpiData.totalCount, {
                    from: dateRange.start,
                    to: dateRange.end
                  });
                }} className="p-2 text-gray-400 hover:text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg transition-colors" title="Descargar PDF" data-id="element-3629">
                      <DownloadIcon className="w-4 h-4" data-id="element-3630" />
                    </button>}
                </div>
              </CardHeader>
              <CardContent className="p-0" data-id="element-3631">
                <div className="overflow-x-auto" data-id="element-3632">
                  <table className="w-full" data-id="element-3633">
                    <thead data-id="element-3634">
                      <tr className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-100 dark:border-gray-700" data-id="element-3635">
                        <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-3636">
                          Servicio
                        </th>
                        <th className="text-right py-4 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-3637">
                          Cantidad
                        </th>
                        <th className="text-right py-4 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-3638">
                          Ingresos Totales
                        </th>
                        <th className="text-right py-4 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-3639">
                          Promedio / Cita
                        </th>
                        <th className="text-right py-4 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-3640">
                          % del Total
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-800" data-id="element-3641">
                      {summaryData.length === 0 ? <tr data-id="element-3642">
                          <td colSpan={5} className="py-12 text-center text-gray-500 dark:text-gray-400" data-id="element-3643">
                            No hay datos para el periodo seleccionado
                          </td>
                        </tr> : paginatedSummary.map((item, idx) => {
                      const percentage = kpiData.totalRevenue > 0 ? item.revenue / kpiData.totalRevenue * 100 : 0;
                      return <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors" data-id="element-3644">
                              <td className="py-4 px-6 text-sm font-medium text-gray-900 dark:text-white" data-id="element-3645">
                                <div className="flex items-center gap-3" data-id="element-3646">
                                  <div className="w-2.5 h-2.5 rounded-full" style={{
                              backgroundColor: compareServiceIds.length > 0 ? COMPARE_COLORS[compareServiceIds.indexOf(services.find(s => s.name === item.name)?.id || '') % COMPARE_COLORS.length] : '#3b82f6'
                            }} data-id="element-3647" />
                                  {item.name}
                                </div>
                              </td>
                              <td className="py-4 px-6 text-sm text-gray-700 dark:text-gray-300 text-right" data-id="element-3648">
                                {item.count}
                              </td>
                              <td className="py-4 px-6 text-sm text-gray-900 dark:text-white font-semibold text-right" data-id="element-3649">
                                {formatCurrency(item.revenue)}
                              </td>
                              <td className="py-4 px-6 text-sm text-gray-500 dark:text-gray-400 text-right" data-id="element-3650">
                                {item.count > 0 ? formatCurrency(item.revenue / item.count) : '-'}
                              </td>
                              <td className="py-4 px-6 text-right" data-id="element-3651">
                                <div className="flex items-center justify-end gap-2" data-id="element-3652">
                                  <span className="text-xs text-gray-500" data-id="element-3653">
                                    {percentage.toFixed(1)}%
                                  </span>
                                  <div className="w-16 h-1.5 bg-gray-100 rounded-full overflow-hidden" data-id="element-3654">
                                    <div className="h-full bg-indigo-500 rounded-full" style={{
                                width: `${percentage}%`
                              }} data-id="element-3655" />
                                  </div>
                                </div>
                              </td>
                            </tr>;
                    })}
                      {/* Total Row */}
                      {summaryData.length > 0 && <tr className="bg-gray-50 dark:bg-gray-800/80 font-bold border-t-2 border-gray-200 dark:border-gray-700" data-id="element-3656">
                          <td className="py-4 px-6 text-sm text-gray-900 dark:text-white" data-id="element-3657">
                            TOTAL
                          </td>
                          <td className="py-4 px-6 text-sm text-gray-900 dark:text-white text-right" data-id="element-3658">
                            {kpiData.totalCount}
                          </td>
                          <td className="py-4 px-6 text-sm text-gray-900 dark:text-white text-right" data-id="element-3659">
                            {formatCurrency(kpiData.totalRevenue)}
                          </td>
                          <td className="py-4 px-6 text-right text-sm text-gray-900 dark:text-white" data-id="element-3660">
                            {formatCurrency(kpiData.avgTicket)}
                          </td>
                          <td className="py-4 px-6 text-right text-sm text-gray-900 dark:text-white" data-id="element-3661">
                            100%
                          </td>
                        </tr>}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Pagination for Summary Table */}
            <Pagination currentPage={summaryPage} totalPages={summaryTotalPages} totalItems={summaryTotalItems} startIndex={summaryStartIndex} endIndex={summaryEndIndex} onPageChange={summaryGoToPage} onNext={summaryNextPage} onPrev={summaryPrevPage} data-id="element-3662" />
          </div>
        </TabPanel>
      </Tabs>

      {/* MODALS (Shared for Management) */}
      <Modal isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} title={selectedService ? 'Editar Servicio' : 'Nuevo Servicio'} size="md" icon={<BriefcaseIcon className="w-5 h-5" data-id="element-3664" />} accentColor="purple" data-id="element-3663">
        <form onSubmit={handleSubmit} className="space-y-6" data-id="element-3665">
          {/* Service Info */}
          <div data-id="element-3666">
            <div className="flex items-center gap-2 mb-4" data-id="element-3667">
              <span className="w-1.5 h-5 rounded-full bg-purple-500" data-id="element-3668"></span>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide" data-id="element-3669">
                Información del Servicio
              </h3>
            </div>
            <div className="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 space-y-4 border border-gray-100 dark:border-gray-700/50" data-id="element-3670">
              <Input label="Nombre del Servicio" value={formData.name} onChange={e => setFormData({
              ...formData,
              name: e.target.value
            })} error={formErrors.name} placeholder="Ej: Consulta General" data-id="element-3671" />
              <Input label="SKU (Código)" value={formData.sku} onChange={e => setFormData({
              ...formData,
              sku: e.target.value.toUpperCase()
            })} placeholder="Ej: SRV-001" data-id="element-3672" />
              <div className="grid grid-cols-2 gap-4" data-id="element-3673">
                <Input type="number" label="Duración (minutos)" value={formData.duration} onChange={e => setFormData({
                ...formData,
                duration: parseInt(e.target.value) || 0
              })} error={formErrors.duration} min={1} data-id="element-3674" />
                <Input type="number" label="Precio (DOP)" value={formData.price} onChange={e => setFormData({
                ...formData,
                price: e.target.value
              })} error={formErrors.price} min={0} step="1" data-id="element-3675" />
              </div>
              <Textarea label="Descripción" value={formData.description} onChange={e => setFormData({
              ...formData,
              description: e.target.value
            })} rows={3} placeholder="Descripción del servicio..." data-id="element-3676" />
            </div>
          </div>

          {/* Doctor Assignment */}
          <div data-id="element-3677">
            <div className="flex items-center gap-2 mb-4" data-id="element-3678">
              <span className="w-1.5 h-5 rounded-full bg-indigo-500" data-id="element-3679"></span>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide" data-id="element-3680">
                Doctores Asignados
              </h3>
            </div>
            <div className="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 border border-gray-100 dark:border-gray-700/50" data-id="element-3681">
              <div className="max-h-48 overflow-y-auto space-y-1" data-id="element-3682">
                {activeDoctors.length === 0 ? <p className="text-sm text-gray-500 italic py-2" data-id="element-3683">
                    No hay doctores activos registrados
                  </p> : activeDoctors.map(doctor => {
                const isSelected = formData.doctorIds.includes(doctor.id);
                return <label key={doctor.id} className={`flex items-center gap-3 p-2.5 rounded-lg cursor-pointer transition-colors ${isSelected ? 'bg-white dark:bg-gray-800 border border-sky-100 dark:border-sky-800/30 shadow-sm' : 'hover:bg-white dark:hover:bg-gray-800 border border-transparent'}`} data-id="element-3684">
                        <div className="relative" data-id="element-3685">
                          <input type="checkbox" checked={isSelected} onChange={() => toggleDoctorSelection(doctor.id)} className="sr-only" data-id="element-3686" />
                          <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all ${isSelected ? 'bg-sky-500 border-sky-500 dark:bg-sky-600 dark:border-sky-600' : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-500'}`} data-id="element-3687">
                            {isSelected && <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3} data-id="element-3688">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" data-id="element-3689" />
                              </svg>}
                          </div>
                        </div>
                        <span className={`text-sm font-medium ${isSelected ? 'text-gray-900 dark:text-white' : 'text-gray-600 dark:text-gray-400'}`} data-id="element-3690">
                          {getDoctorPrefix(doctor.gender, getSpecialty(doctor.specialtyId)?.name)}{' '}
                          {doctor.name}
                        </span>
                      </label>;
              })}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-gray-700" data-id="element-3691">
            <Button variant="outline" type="button" onClick={() => setIsFormOpen(false)} data-id="element-3692">
              Cancelar
            </Button>
            <Button type="submit" data-id="element-3693">
              {selectedService ? 'Guardar Cambios' : 'Crear Servicio'}
            </Button>
          </div>
        </form>
      </Modal>

      <Modal isOpen={isActionConfirmOpen} onClose={() => setIsActionConfirmOpen(false)} title={actionType === 'archive' ? 'Archivar Servicio' : actionType === 'deactivate' ? 'Desactivar Servicio' : 'Eliminar Servicio'} size="sm" data-id="element-3694">
        <div className="space-y-4" data-id="element-3695">
          <p className="text-gray-600 dark:text-gray-400" data-id="element-3696">
            {actionType === 'archive' && `¿Estás seguro de que deseas archivar a "${selectedService?.name}"? Podrás restaurarlo más tarde.`}
            {actionType === 'deactivate' && `¿Estás seguro de que deseas desactivar a "${selectedService?.name}"? No aparecerá en las listas activas.`}
            {actionType === 'delete' && `¿Estás seguro de que deseas eliminar permanentemente a "${selectedService?.name}"? Esta acción no se puede deshacer.`}
          </p>
          <div className="flex justify-end gap-3" data-id="element-3697">
            <Button variant="outline" onClick={() => setIsActionConfirmOpen(false)} data-id="element-3698">
              Cancelar
            </Button>
            <Button variant={actionType === 'delete' ? 'danger' : 'primary'} onClick={handleAction} data-id="element-3699">
              {actionType === 'archive' ? 'Archivar' : actionType === 'deactivate' ? 'Desactivar' : 'Eliminar'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Excel Export Modal */}
      <ExcelExportModal isOpen={isExportModalOpen} onClose={() => setIsExportModalOpen(false)} onExport={handleExportToExcel} title="Exportar Servicios" isExporting={isExporting} data-id="element-3700" />

      {/* PDF Export Modal */}
      <PdfExportModal isOpen={isPdfExportModalOpen} onClose={() => setIsPdfExportModalOpen(false)} onExport={handleExportToPdf} title="Exportar Servicios a PDF" isExporting={isPdfExporting} data-id="element-3701" />
    </div>;
}